import React from 'react';

import { Button, Group, Modal, ModalProps, Stack, Text, Title } from '@mantine/core';
import { AxiosError, AxiosResponse } from 'axios';
import KiloAlert from 'components/KiloAlert';
import useGoogleCalendar from 'hooks/googleCalendar/useGoogleCalendar';
import { UseMutationResult } from 'react-query';
import { ErrorMessage, GoogleCalendarIntegration, GoogleCalendarIntegrationUpdateSynced } from 'types';

interface SyncedCalendarChangeModalProps extends Pick<ModalProps, 'opened' | 'onClose'> {
  googleCalendarIntegration: GoogleCalendarIntegration;
  syncedCalendarId: string | null;
  updateSyncedCalendarMutation: UseMutationResult<
    AxiosResponse<GoogleCalendarIntegration>,
    AxiosError<ErrorMessage>,
    GoogleCalendarIntegrationUpdateSynced
  >;
}

const SyncedCalendarChangeModal = ({
  opened,
  onClose,
  googleCalendarIntegration,
  syncedCalendarId,
  updateSyncedCalendarMutation,
}: SyncedCalendarChangeModalProps): JSX.Element => {
  const isCreate = googleCalendarIntegration.syncedCalendar === null;
  const showAppointments = googleCalendarIntegration.syncAppointmentsToCalendar;
  const showClasses = googleCalendarIntegration.syncClassesToCalendar;

  const eventText = [];
  if (showAppointments) eventText.push('appointments');
  if (showClasses) eventText.push('classes');

  const { data } = useGoogleCalendar(syncedCalendarId);
  const newCalendar = data?.data.calendarName || '';

  return (
    <Modal
      centered
      opened={opened}
      onClose={onClose}
      size='lg'
      title={<Title order={3}>{isCreate ? 'Add' : 'Change'} Synced Calendar</Title>}
    >
      <Stack>
        <Stack px='md'>
          <Text size='lg'>
            This will add all selected upcoming events <b>({eventText.join(' and ')})</b> to your Google
            calendar <b>{newCalendar}</b>.
          </Text>
          {!isCreate && (
            <Text size='lg'>
              Additionally, this will remove all upcoming events from your previous calendar{' '}
              <b>{googleCalendarIntegration.syncedCalendar.calendarName}</b>.
            </Text>
          )}
          <KiloAlert>It may take some time for your Google Calendar to update</KiloAlert>
        </Stack>

        <Group mt='lg' position='right'>
          <Button onClick={onClose} variant='outline'>
            Cancel
          </Button>
          <Button form='synced-calendar-form' type='submit' loading={updateSyncedCalendarMutation.isLoading}>
            Confirm
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};

export default SyncedCalendarChangeModal;
